<template>
    <base-tool
        :tool-name="tool.name"
        :tool-description="tool.description"
        @back="goBack"
    >
        <div 
            v-if="loadingPipelines" 
            class="text-center py-3"
            data-testid="hubspot-owner-tool-loading"
        >
            <i class="el-icon-loading"></i> Loading HubSpot pipelines...
        </div>

        <div 
            v-else-if="hubspotError" 
            class="alert alert-danger"
            data-testid="hubspot-owner-tool-error"
        >
            <i class="el-icon-warning"></i> {{ hubspotError }}
        </div>

        <div v-else data-testid="hubspot-owner-tool-form">
            <div class="form-group">
                <label data-testid="hubspot-owner-tool-pipelines-label">HubSpot Deal Pipelines <span class="text-danger">*</span></label>
                <el-select
                    v-model="selectedPipelines"
                    data-testid="hubspot-owner-tool-pipelines-select"
                    multiple
                    filterable
                    placeholder="Select one or more pipelines"
                    class="w-100"
                    @change="handlePipelineChange"
                >
                    <el-option
                        v-for="pipeline in activePipelines"
                        :key="pipeline.pipelineId"
                        :label="pipeline.label"
                        :value="pipeline.pipelineId"
                        :data-testid="`hubspot-owner-tool-pipeline-option-${pipeline.pipelineId}`"
                    >
                        <span>{{ pipeline.label }}</span>
                    </el-option>
                </el-select>
                <small 
                    class="form-text text-muted"
                    data-testid="hubspot-owner-tool-pipelines-help"
                >
                    Select one or more existing HubSpot pipeline IDs to search for deal contact owners
                </small>
                <div 
                    v-if="showPipelineError" 
                    class="text-danger small mt-1"
                    data-testid="hubspot-owner-tool-pipelines-error"
                >
                    Please select at least one pipeline
                </div>
            </div>

            <div class="form-group mt-3">
                <label data-testid="hubspot-owner-tool-ring-group-label">Fallback Inbox <span class="text-danger">*</span></label>
                <ring-group-selector
                    v-model="fallbackRingGroupId"
                    data-testid="hubspot-owner-tool-ring-group-selector"
                    :placeholder="'Select a fallback inbox'"
                    @change="handleRingGroupChange"
                />
                <small 
                    class="form-text text-muted"
                    data-testid="hubspot-owner-tool-ring-group-help"
                >
                    Select an existing inbox ID to route to if no contact owner is found
                </small>
                <div 
                    v-if="showRingGroupError" 
                    class="text-danger small mt-1"
                    data-testid="hubspot-owner-tool-ring-group-error"
                >
                    Please select a fallback inbox
                </div>
            </div>

            <!-- Action button -->
            <div class="mt-3">
                <el-button
                    data-testid="hubspot-owner-tool-add-button"
                    type="primary"
                    @click="addTool"
                    :disabled="!isValid"
                >
                    Add {{ tool.name }}
                </el-button>
            </div>
        </div>
    </base-tool>
</template>

<script>
import BaseTool from './base-tool.vue'
import RingGroupSelector from '../../../ring-group-selector'
import ToolMixin from './tool-mixin'

export default {
    name: 'HubspotOwnerTool',

    mixins: [ToolMixin],

    components: {
        BaseTool,
        RingGroupSelector,
    },

    props: {
        tool: {
            type: Object,
            required: true
        }
    },

    data() {
        return {
            selectedPipelines: [],
            pipelines: [],
            fallbackRingGroupId: null,
            showPipelineError: false,
            showRingGroupError: false,
            loadingPipelines: true,
            hubspotError: null,
        }
    },

    computed: {
        activePipelines() {
            // Filter only active pipelines
            return this.pipelines.filter(p => p.active)
        },

        toolPattern() {
            if (!this.selectedPipelines || this.selectedPipelines.length === 0) {
                return ''
            }

            // Use only pipeline IDs (no names)
            const pipelineIds = this.selectedPipelines.join(',')
            let text = `{Route to HubSpot deal contact owner: ${pipelineIds}`
            
            // Add fallback ring group ID only (no name)
            if (this.fallbackRingGroupId) {
                text += `|${this.fallbackRingGroupId}`
            }
            
            text += '}'
            return text
        },

        isValid() {
            return this.selectedPipelines 
                && this.selectedPipelines.length > 0 
                && this.fallbackRingGroupId 
                && !this.loadingPipelines 
                && !this.hubspotError
        }
    },

    mounted() {
        this.loadHubSpotPipelines()
    },

    methods: {
        handlePipelineChange() {
            // Reset error when user selects
            this.showPipelineError = false
        },

        handleRingGroupChange(ringGroupId) {
            this.fallbackRingGroupId = ringGroupId
            // Reset error when user selects
            this.showRingGroupError = false
        },

        async loadHubSpotPipelines() {
            this.loadingPipelines = true
            this.hubspotError = null

            try {
                const response = await window.axios.get('/api/v1/integration/hubspot/deal-pipelines')
                
                if (response.data && response.data.pipelines) {
                    this.pipelines = response.data.pipelines
                    
                    if (this.pipelines.length === 0) {
                        this.hubspotError = 'No HubSpot pipelines found. Please configure your HubSpot integration first.'
                    }
                } else {
                    this.hubspotError = 'Unable to load HubSpot pipelines. Please check your HubSpot integration.'
                }
            } catch (error) {
                console.error('Error loading HubSpot pipelines:', error)
                
                if (error.response && error.response.status === 404) {
                    this.hubspotError = 'HubSpot integration not found. Please connect your HubSpot account first.'
                } else if (error.response && error.response.status === 403) {
                    this.hubspotError = 'HubSpot integration is not enabled for your account.'
                } else {
                    this.hubspotError = 'Failed to load HubSpot pipelines. Please try again or contact support.'
                }
            } finally {
                this.loadingPipelines = false
            }
        },

        addTool() {
            // Reset errors
            this.showPipelineError = false
            this.showRingGroupError = false

            // Validate pipeline selection
            if (!this.selectedPipelines || this.selectedPipelines.length === 0) {
                this.showPipelineError = true
                this.$message.error('Please select at least one HubSpot pipeline')
                return
            }

            // Validate Inbox selection
            if (!this.fallbackRingGroupId) {
                this.showRingGroupError = true
                this.$message.error('Please select a fallback Inbox')
                return
            }

            this.$emit('hubspot-owner-selected', {
                text: this.toolPattern
            })
        }
    }
}
</script>

<style scoped>
.form-group {
    margin-bottom: 15px;
}

.alert {
    padding: 12px;
    border-radius: 4px;
    margin-bottom: 15px;
}

.alert-danger {
    background-color: #f8d7da;
    border: 1px solid #f5c6cb;
    color: #721c24;
}
</style>

